import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ShoppingCart, Search, Plus, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, Calendar, Package, Loader2, FileDown
} from 'lucide-react';
import CreateButton from '../components/CreateButton';

const PRIMARY = '#0099FF';
const ITEMS_PER_PAGE = 20;
const IVA_MAP = { 1: 4, 2: 10, 3: 21 };

const ESTADO_CONFIG = {
  1: { label: 'Abierto', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  2: { label: 'Confirmado', bg: 'bg-blue-100', text: 'text-blue-700' },
  3: { label: 'Enviado', bg: 'bg-green-100', text: 'text-green-700' },
  4: { label: 'Envío parcial', bg: 'bg-orange-100', text: 'text-orange-800' },
};

const formatCurrency = (value) => {
  if (value == null) return '—';
  return Number(value).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ClientePedidos = ({ embedded = false }) => {
  const { token, API_URL, activeClient } = useAuth();

  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);

  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);

  const totalPages = Math.ceil(totalOrders / ITEMS_PER_PAGE);

  const fetchOrders = useCallback(async (currentPage = 1, search = '') => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });
      if (search.trim()) params.append('search', search.trim());

      const res = await fetch(`${API_URL}/orders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar pedidos');
      const data = await res.json();
      setOrders(data.orders || []);
      setTotalOrders(data.pagination?.total || data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchOrders(page, searchTerm);
    // Re-cargar cuando cambia el cliente activo (selector lateral).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeClient?.id]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setPage(1);
      fetchOrders(1, searchTerm);
    }, 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const handleExpandRow = async (orderId) => {
    if (expandedRow === orderId) {
      setExpandedRow(null);
      setExpandedItems([]);
      setExpandedOrder(null);
      return;
    }
    setExpandedRow(orderId);
    setLoadingItems(true);
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar detalles');
      const data = await res.json();
      const order = data.order || data;
      setExpandedItems(order.lineas || order.items || []);
      setExpandedOrder(order);
    } catch (err) {
      console.error(err);
      setExpandedItems([]);
      setExpandedOrder(null);
    } finally {
      setLoadingItems(false);
    }
  };

  // PDF download handler
  const handlePdfDownload = async (e, orderId) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al descargar PDF');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `pedido_${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setError(err.message);
    }
  };

  // Descarga Excel/CSV (solo lectura para el cliente: el backend NO cambia el
  // estado del pedido cuando lo descarga un cliente, a diferencia del admin).
  const handleCsvDownload = async (e, orderId) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/csv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al descargar Excel');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `pedido_${orderId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className={embedded ? 'p-6' : 'h-full overflow-y-auto p-6'}>

      {/* Header — solo visible cuando NO está embebido en pestañas */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <ShoppingCart className="w-5 h-5" style={{ color: PRIMARY }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Mis Pedidos</h1>
              <p className="text-sm text-gray-500">{totalOrders} pedidos en total</p>
            </div>
          </div>
          <CreateButton to="/cliente/nuevo-pedido">Nuevo Pedido</CreateButton>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por N° de pedido o fecha..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8" />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Pedido</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">N° Líneas</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">PDF</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-sm text-gray-500 mt-2">Cargando pedidos...</p>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No se encontraron pedidos</p>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const estado = ESTADO_CONFIG[order.estado] || { label: 'Desconocido', bg: 'bg-gray-100', text: 'text-gray-600' };
                  const isExpanded = expandedRow === order.id;

                  return (
                    <React.Fragment key={order.id}>
                      <tr
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleExpandRow(order.id)}
                      >
                        <td className="px-6 py-4">
                          {isExpanded
                            ? <ChevronUp size={16} className="text-gray-400" />
                            : <ChevronDown size={16} className="text-gray-400" />
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">#{order.npedido || order.id}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar size={14} className="mr-2 text-gray-400" />
                            {formatDate(order.fecha)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estado.bg} ${estado.text}`}>
                            {estado.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-600">{order.total_lineas ?? order.num_lineas ?? '—'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(order.itotaliva ?? order.total)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={(e) => handlePdfDownload(e, order.id)}
                              className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                              title="Descargar PDF"
                            >
                              <FileDown size={16} />
                            </button>
                            {order.estado !== 1 && (
                              <button
                                onClick={(e) => handleCsvDownload(e, order.id)}
                                className="p-1.5 rounded text-gray-400 hover:text-green-600 transition-colors"
                                title="Descargar Excel (CSV)"
                              >
                                <FileDown size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 bg-gray-50">
                            {loadingItems ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                <span className="ml-2 text-sm text-gray-500">Cargando detalle...</span>
                              </div>
                            ) : expandedItems.length === 0 ? (
                              <p className="text-sm text-gray-500 text-center py-2">Sin líneas de detalle</p>
                            ) : (
                              <table className="min-w-full">
                                <thead>
                                  <tr className="text-xs text-gray-500 uppercase">
                                    <th className="py-2 text-left font-medium">Producto</th>
                                    <th className="py-2 text-center font-medium">Cantidad</th>
                                    <th className="py-2 text-right font-medium">Precio Ud.</th>
                                    <th className="py-2 text-right font-medium">IVA</th>
                                    <th className="py-2 text-right font-medium">Subtotal</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {expandedItems.map((item, idx) => {
                                    const lineSubtotal = item.subtotal != null
                                      ? Number(item.subtotal)
                                      : (item.cantidad * item.precio);
                                    const ivaPercent = item.iva != null ? Number(item.iva) : 0;
                                    return (
                                      <tr key={item.id || idx}>
                                        <td className="py-2 text-sm text-gray-700">
                                          {item.nombre || item.producto_nombre || `Producto #${item.idarticulo}`}
                                        </td>
                                        <td className="py-2 text-sm text-gray-600 text-center">{item.cantidad}</td>
                                        <td className="py-2 text-sm text-gray-600 text-right">{formatCurrency(item.precio)}</td>
                                        <td className="py-2 text-sm text-gray-600 text-right">{ivaPercent}%</td>
                                        <td className="py-2 text-sm font-medium text-gray-900 text-right">
                                          {formatCurrency(lineSubtotal)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot className="border-t-2 border-gray-300">
                                  {(() => {
                                    const baseImponible = expandedOrder?.itotal != null
                                      ? Number(expandedOrder.itotal)
                                      : expandedItems.reduce((sum, item) => {
                                          const sub = item.subtotal != null ? Number(item.subtotal) : (item.cantidad * item.precio);
                                          return sum + sub;
                                        }, 0);
                                    const totalConIva = expandedOrder?.itotaliva != null
                                      ? Number(expandedOrder.itotaliva)
                                      : expandedItems.reduce((sum, item) => {
                                          const sub = item.subtotal != null ? Number(item.subtotal) : (item.cantidad * item.precio);
                                          const ivaPct = item.iva != null ? Number(item.iva) : 0;
                                          return sum + sub * (1 + ivaPct / 100);
                                        }, 0);
                                    const ivaAmount = totalConIva - baseImponible;
                                    return (
                                      <>
                                        <tr>
                                          <td colSpan="4" className="py-2 text-sm text-gray-600 text-right font-medium">Base imponible:</td>
                                          <td className="py-2 text-sm text-gray-900 text-right font-medium">{formatCurrency(baseImponible)}</td>
                                        </tr>
                                        <tr>
                                          <td colSpan="4" className="py-1 text-sm text-gray-600 text-right font-medium">IVA:</td>
                                          <td className="py-1 text-sm text-gray-900 text-right font-medium">{formatCurrency(ivaAmount)}</td>
                                        </tr>
                                        <tr>
                                          <td colSpan="4" className="py-2 text-sm text-gray-900 text-right font-bold">Total:</td>
                                          <td className="py-2 text-sm text-gray-900 text-right font-bold">{formatCurrency(totalConIva)}</td>
                                        </tr>
                                      </>
                                    );
                                  })()}
                                </tfoot>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Página {page} de {totalPages} · {totalOrders} pedidos
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientePedidos;
